import { getApiClient } from "@src/api";
import { navbarBranchQueryKeys } from "@src/components/board/queries/fetch-ranklist";
import { whiteboardQueryKeys } from "@src/components/board/queries/query-keys";
import { graphql } from "@src/gql/gql";
import { queryClient } from "@src/query-client";
import { useMutation } from "@tanstack/react-query";
import type { Whiteboard } from "../schema";

const publishWhiteboardQuery = graphql(/* GraphQL */ `
	mutation publishWhiteboard($whiteboardId: String!, $isReadonly: Boolean, $allowClone: Boolean) {
		whiteboards {
			publish(
				whiteboardId: $whiteboardId
				isReadonly: $isReadonly
				allowClone: $allowClone
			) {
				state {
					name
					ownerId
					status
					permissions {
						publishedLink {
							allowClone
							isReadonly
							key
						}
						userPermissions {
							key
							value
						}
					}
				}
			}
		}
	}
`);

/**
 * Publish a whiteboard, generating a shareable link.
 *
 * @returns React Query mutation object
 */
export const usePublishWhiteboard = () => {
	const client = getApiClient();

	return useMutation({
		mutationFn: async ({
			whiteboardId,
			isReadonly,
			allowClone,
		}: {
			whiteboardId: string;
			isReadonly: boolean;
			allowClone: boolean;
		}) => {
			const result = await client.mutate({
				mutation: publishWhiteboardQuery,
				variables: {
					whiteboardId,
					isReadonly,
					allowClone,
				},
			});

			if (result.errors && result.errors.length > 0) {
				throw new Error(result.errors[0].message);
			}

			return result.data;
		},
		onSuccess: (data, variables) => {
			const queryData = queryClient.getQueryData<Whiteboard>(
				whiteboardQueryKeys.ofId(variables.whiteboardId),
			);

			const publication =
				data?.whiteboards?.publish?.state?.permissions.publishedLink;

			if (queryData && publication) {
				queryClient.setQueryData(
					whiteboardQueryKeys.ofId(variables.whiteboardId),
					{
						...queryData,
						publication: {
							isPublished: true,
							key: publication.key,
							allowClone: publication.allowClone,
							isReadonly: publication.isReadonly,
						},
					},
				);
			}

			queryClient.invalidateQueries({
				queryKey: navbarBranchQueryKeys.all(),
			});
			queryClient.invalidateQueries({
				queryKey: whiteboardQueryKeys.ofId(variables.whiteboardId),
			});
		},
	});
};
